import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, PROGRAM_LABELS, formatErr } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export function ReportsPage() {
  const base = process.env.REACT_APP_BACKEND_URL;
  const [rev, setRev] = useState({ rows: [] });
  useEffect(() => { api.get("/admin/reports/revenue-summary").then(({ data }) => setRev(data)).catch(() => {}); }, []);
  const EXPORTS = [
    { id: "bookings", label: "All Bookings", hint: "Every registration with UTM + payment fields.", path: "export-bookings" },
    { id: "programs", label: "Programs & Slots", hint: "Slots with capacity, markets, paid bookings & pricing.", path: "export-programs" },
    { id: "sources", label: "Sources / UTM", hint: "Lead sources with totals, paid count & revenue.", path: "export-sources" },
    { id: "revenue", label: "Revenue Breakdown", hint: "Paid revenue by program, gateway & currency.", path: "export-revenue" },
  ];
  const totals = rev.rows.reduce((a, r) => { a[r.currency] = (a[r.currency] || 0) + r.revenue; return a; }, {});
  return (
    <div className="space-y-6 fade-up" data-testid="reports-page">
      <h1 className="font-serif text-4xl">Reports &amp; Export</h1>
      <p className="text-sm text-[#7E736E]">Download CSV exports for analysis and reconciliation.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {EXPORTS.map(e => (
          <a key={e.id} href={`${base}/api/admin/reports/${e.path}`} target="_blank" rel="noreferrer"
            className="soft-card hover:-translate-y-1 transition" data-testid={`export-${e.id}`}>
            <div className="text-xs uppercase tracking-wider text-terracotta">CSV Export</div>
            <div className="font-serif text-2xl mt-2">{e.label}</div>
            <div className="text-xs text-[#7E736E] mt-2">{e.hint}</div>
          </a>
        ))}
      </div>

      <div className="soft-card !p-6" data-testid="revenue-summary">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="font-serif text-2xl">Revenue summary (paid)</div>
          <div className="flex gap-4 text-sm">
            <span>India: <b>₹{(totals.INR || 0).toLocaleString("en-IN")}</b></span>
            <span>Global: <b>${(totals.USD || 0).toLocaleString("en-US")}</b></span>
          </div>
        </div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-[#7E736E]">
              <tr><th className="text-left py-2">Program</th><th className="text-left">Currency</th><th className="text-left">Bookings</th><th className="text-left">Revenue</th></tr>
            </thead>
            <tbody>
              {rev.rows.map((r, i) => (
                <tr key={i} className="border-t border-[#EBE5D9]">
                  <td className="py-2">{PROGRAM_LABELS[r.program_type] || r.program_type}</td>
                  <td>{r.currency}</td><td>{r.bookings}</td>
                  <td>{r.currency === "INR" ? "₹" : "$"}{r.revenue.toLocaleString()}</td>
                </tr>
              ))}
              {rev.rows.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-[#7E736E]">No paid revenue yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const PRICED_PROGRAMS = ["meditation_enlightenment", "clarity_call", "bliss_path", "aura_night", "one_to_one"];
const INTEGRATIONS = [
  { key: "razorpay", label: "Razorpay (India / INR)", fields: ["key_id", "key_secret"] },
  { key: "paypal", label: "PayPal (Global / USD)", fields: ["client_id", "client_secret"] },
  { key: "zoom", label: "Zoom Meetings", fields: ["account_id", "client_id", "client_secret"] },
  { key: "email", label: "Email Provider", fields: ["provider", "api_key", "from_email"] },
  { key: "whatsapp", label: "WhatsApp Business", fields: ["provider", "phone_id", "token"] },
];

export function SettingsPage() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [timezones, setTimezones] = useState([]);
  const [saving, setSaving] = useState("");

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setS(data)).catch(e => toast.error(formatErr(e)));
    api.get("/admin/master/timezones").then(({ data }) => setTimezones(data)).catch(() => {});
  }, []);

  const setField = (section, key, value) =>
    setS(prev => ({ ...prev, [section]: { ...(prev[section] || {}), [key]: value } }));
  const setNested = (section, sub, key, value) =>
    setS(prev => ({ ...prev, [section]: { ...(prev[section] || {}), [sub]: { ...((prev[section] || {})[sub] || {}), [key]: value } } }));

  const save = async (section) => {
    setSaving(section);
    try {
      const { data } = await api.put("/admin/settings", { [section]: s[section] });
      setS(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(""); }
  };

  if (!s) return <div className="text-[#7E736E]" data-testid="settings-loading">Loading settings…</div>;

  const SaveBtn = ({ section }) => (
    <button onClick={() => save(section)} disabled={saving === section}
      className="btn-primary text-sm mt-2" data-testid={`save-${section}`}>
      {saving === section ? "Saving…" : "Save changes"}
    </button>
  );

  return (
    <div className="space-y-6 fade-up" data-testid="settings-page">
      <div>
        <h1 className="font-serif text-4xl">Settings</h1>
        <p className="text-sm text-[#7E736E] mt-1">Signed in as {user?.email} · {user?.role}</p>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="bg-[#F3EFEA]">
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="tab-pricing">Pricing</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
        </TabsList>

        {/* General */}
        <TabsContent value="general">
          <div className="soft-card space-y-4 max-w-2xl">
            <div className="font-serif text-2xl">General</div>
            {[["site_name","Site name"],["support_email","Support email"],["support_whatsapp","Support WhatsApp"],
              ["india_domain","India domain (.in → INR / Razorpay)"],["global_domain","Global domain (.com → USD / PayPal)"]].map(([k,l])=>(
              <div key={k}>
                <Label className="text-xs uppercase text-[#7E736E]">{l}</Label>
                <Input value={s.general?.[k] || ""} onChange={e=>setField("general",k,e.target.value)} data-testid={`general-${k}`}/>
              </div>
            ))}
            <div>
              <Label className="text-xs uppercase text-[#7E736E]">Default timezone</Label>
              <Select value={s.general?.default_timezone} onValueChange={v=>setField("general","default_timezone",v)}>
                <SelectTrigger data-testid="general-timezone"><SelectValue/></SelectTrigger>
                <SelectContent>{timezones.map(tz=><SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <SaveBtn section="general"/>
          </div>
        </TabsContent>

        {/* Pricing */}
        <TabsContent value="pricing">
          <div className="soft-card space-y-4 max-w-2xl">
            <div className="font-serif text-2xl">Pricing</div>
            <p className="text-sm text-[#7E736E]">INR applies to the India (.in) market, USD to the Global (.com) market.</p>
            <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-center text-xs uppercase text-[#7E736E]">
              <div>Program</div><div className="w-28 text-center">₹ INR</div><div className="w-28 text-center">$ USD</div>
            </div>
            {PRICED_PROGRAMS.map(p=>(
              <div key={p} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center">
                <div className="text-sm">{PROGRAM_LABELS[p]}</div>
                <Input type="number" className="w-28" value={s.pricing?.[p]?.inr ?? ""} onChange={e=>setNested("pricing",p,"inr",Number(e.target.value))} data-testid={`price-${p}-inr`}/>
                <Input type="number" className="w-28" value={s.pricing?.[p]?.usd ?? ""} onChange={e=>setNested("pricing",p,"usd",Number(e.target.value))} data-testid={`price-${p}-usd`}/>
              </div>
            ))}
            <SaveBtn section="pricing"/>
          </div>
        </TabsContent>

        {/* Integrations */}
        <TabsContent value="integrations">
          <div className="space-y-4 max-w-2xl">
            <p className="text-sm text-[#7E736E]">All integrations run in <b>Mock</b> mode. Toggle to Live and paste credentials when ready (no real calls are made yet).</p>
            {INTEGRATIONS.map(intg=>{
              const cfg = s.integrations?.[intg.key] || {};
              const live = cfg.mode === "live";
              return (
                <div key={intg.key} className="soft-card space-y-3" data-testid={`integration-${intg.key}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-serif text-xl">{intg.label}</div>
                    <div className="flex items-center gap-2">
                      <span className={`badge-soft ${live?"bg-[#D4E4D3] text-[#2F4F2F]":"bg-[#F9E8C0] text-[#7A5C1F]"}`}>{live?"Live":"Mock"}</span>
                      <Switch checked={live} onCheckedChange={c=>setNested("integrations",intg.key,"mode",c?"live":"mock")} data-testid={`toggle-${intg.key}`}/>
                    </div>
                  </div>
                  {intg.fields.map(f=>(
                    <div key={f}>
                      <Label className="text-xs uppercase text-[#7E736E]">{f.replace(/_/g," ")}</Label>
                      <Input value={cfg[f] || ""} placeholder={`Enter ${f.replace(/_/g," ")}`}
                        onChange={e=>setNested("integrations",intg.key,f,e.target.value)} data-testid={`${intg.key}-${f}`}/>
                    </div>
                  ))}
                </div>
              );
            })}
            <SaveBtn section="integrations"/>
          </div>
        </TabsContent>

        {/* Security */}
        <TabsContent value="security">
          <div className="soft-card space-y-4 max-w-2xl">
            <div className="font-serif text-2xl">Security</div>
            <div>
              <Label className="text-xs uppercase text-[#7E736E]">Session length (hours)</Label>
              <Input type="number" value={s.security?.session_hours ?? ""} onChange={e=>setField("security","session_hours",Number(e.target.value))} data-testid="security-session-hours"/>
            </div>
            <div>
              <Label className="text-xs uppercase text-[#7E736E]">Max login attempts</Label>
              <Input type="number" value={s.security?.max_login_attempts ?? ""} onChange={e=>setField("security","max_login_attempts",Number(e.target.value))} data-testid="security-max-attempts"/>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Require email consent at registration</Label>
              <Switch checked={!!s.security?.require_email_consent} onCheckedChange={c=>setField("security","require_email_consent",c)} data-testid="security-email-consent"/>
            </div>
            <SaveBtn section="security"/>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
